import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// Sticky top banner shown while an admin/ops is impersonating a client.
// Clicking "End preview" calls /api/impersonation/end which swaps the
// session cookie back to the admin's original token, then redirects to
// /admin/accounts.
export default function ImpersonationBanner() {
  const { user, account, impersonator, endImpersonation } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!impersonator) return null;

  const onEnd = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await endImpersonation();
      navigate(res?.redirect || '/admin/accounts', { replace: true });
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Failed to end preview');
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-40 bg-[#B0FC38] text-gray-900 px-4 py-2.5 flex items-center justify-between gap-3 shadow-lg"
    >
      <div className="flex items-center gap-2 text-[13px] min-w-0">
        <Eye className="w-4 h-4 shrink-0" />
        <span className="truncate">
          <span className="font-semibold">Previewing</span> as{' '}
          <span className="font-mono">{user?.email}</span>
          {account?.name && <> at <span className="font-semibold">{account.name}</span></>}{' '}
          <span className="opacity-80">· signed in by {impersonator.email}</span>
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {error && <span className="text-[11px] bg-red-500/30 px-2 py-0.5 rounded">{error}</span>}
        <button
          type="button"
          data-testid="impersonation-end-button"
          onClick={onEnd}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/10 hover:bg-black/20 text-[12px] font-semibold disabled:opacity-50"
        >
          <X className="w-3 h-3" /> {busy ? 'Ending…' : 'End preview'}
        </button>
      </div>
    </div>
  );
}
